import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, WarningCircle, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";
import { RSpinBlock } from "@/components/RSpin";
import { toast } from "sonner";

const NETWORK_BADGE = {
  impact:     "bg-violet-100 text-violet-800",
  skimlinks:  "bg-emerald-100 text-emerald-800",
  mrge:       "bg-orange-100 text-orange-800",
  partnerize: "bg-blue-100 text-blue-800",
  yieldkit:   "bg-rose-100 text-rose-800",
  admedia:    "bg-violet-100 text-violet-800",
  shopnomix:  "bg-amber-100 text-amber-800",
  cj:         "bg-teal-100 text-teal-800",
  flexoffers: "bg-indigo-100 text-indigo-800",
  shopnomix_kevin: "bg-fuchsia-100 text-fuchsia-800",
  siteplug:   "bg-sky-100 text-sky-800",
  maxbounty:  "bg-cyan-100 text-cyan-800",
  cashbackhive: "bg-teal-100 text-teal-800",
  fatcoupon:  "bg-rose-100 text-rose-800",
  giddyup:    "bg-yellow-100 text-yellow-800",
  pubadhub:   "bg-cyan-100 text-cyan-800",
  pubadhub_kevin: "bg-teal-100 text-teal-800",
  pronomix:   "bg-lime-100 text-lime-800",
  rga:        "bg-red-100 text-red-800",
  "":         "bg-slate-100 text-slate-600",
};

/**
 * ApiPreviewPanel — admin-only diagnostic that mirrors `/v1/brands` for
 * the publisher, showing visible vs filtered brands + reasons.
 *
 * Why this exists: pubs occasionally report "I see X in the dashboard
 * but not in the API". The dashboard returns every assignment; the
 * public API runs additional filters (draft flip, ?network=, etc.).
 * This panel lets admin simulate the pub's exact API call (matching
 * their `?include_draft=` + `?network=` params) and see the diff
 * instantly — no need to re-implement the filter logic by hand.
 */
export function ApiPreviewPanel({ publisherId }) {
  const [includeDraft, setIncludeDraft] = useState(true);
  const [networkInput, setNetworkInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOnlyFiltered, setShowOnlyFiltered] = useState(false);
  // Per-row freshness re-check state — keyed by brand_id.
  const [recheckingIds, setRecheckingIds] = useState(new Set());

  const recheckOne = async (brandId) => {
    setRecheckingIds((prev) => new Set(prev).add(brandId));
    try {
      const { data: r } = await API.post(`/admin/maintenance/recheck-brand/${brandId}`);
      if (!r.ok) {
        toast.error(`Re-check failed: ${r.error || "upstream error"}`);
      } else if (r.rate_changed || r.status_changed) {
        toast.success(
          r.rate_changed
            ? `Rate updated: ${r.prev_rate}% → ${r.new_rate}%`
            : `Status: ${r.prev_status} → ${r.new_status}`
        );
        await runPreview();  // refresh table to show new freshness
      } else {
        toast.info(`No change — rate ${r.new_rate}%, status ${r.new_status}`);
        await runPreview();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-check failed");
    } finally {
      setRecheckingIds((prev) => {
        const n = new Set(prev);
        n.delete(brandId);
        return n;
      });
    }
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("include_draft", includeDraft ? "1" : "0");
      if (networkInput.trim()) params.set("network", networkInput.trim());
      const { data } = await API.get(
        `/admin/publishers/${publisherId}/api-preview?${params.toString()}`,
      );
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = data?.rows.filter((r) => !showOnlyFiltered || !r.visible) || [];

  return (
    <div className="border border-slate-200 rounded-md" data-testid="api-preview-panel">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">
            API Visibility Diagnostic
          </p>
          <h3 className="font-heading text-base font-semibold text-slate-900 mt-0.5">
            What /v1/brands returns for this publisher
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Simulate the publisher's external API call. Mirrors the exact
            filters in <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">/v1/brands</code>.
            Useful when a pub reports "I see X in the dashboard but not in the API".
          </p>
        </div>
      </div>

      {/* Query controls — same params Omar / other ingestion partners can pass */}
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/40 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-3 space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            include_draft
          </Label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIncludeDraft(true)}
              data-testid="api-preview-draft-1"
              className={`h-8 px-3 text-xs font-semibold rounded-full border ${
                includeDraft
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              1 (default)
            </button>
            <button
              type="button"
              onClick={() => setIncludeDraft(false)}
              data-testid="api-preview-draft-0"
              className={`h-8 px-3 text-xs font-semibold rounded-full border ${
                !includeDraft
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              0 (strict)
            </button>
          </div>
        </div>
        <div className="md:col-span-6 space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            network filter <span className="font-normal normal-case text-slate-400">— comma-separated, leave blank for all</span>
          </Label>
          <Input
            value={networkInput}
            onChange={(e) => setNetworkInput(e.target.value)}
            placeholder="e.g. impact,skimlinks,mrge"
            className="h-9 text-sm font-mono"
            data-testid="api-preview-network-input"
          />
        </div>
        <div className="md:col-span-3 flex md:justify-end">
          <Button
            onClick={runPreview}
            disabled={loading}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white w-full md:w-auto"
            data-testid="api-preview-run-btn"
          >
            <MagnifyingGlass size={14} className="mr-1.5" />
            {loading ? "Running…" : "Run API preview"}
          </Button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <RSpinBlock label="Running /v1/brands simulation…" height={120} />
      ) : !data ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">
          Set the params above and hit <b>Run API preview</b> to see what this publisher
          would see when calling <code className="font-mono bg-slate-100 px-1 rounded">/v1/brands</code>.
        </div>
      ) : (
        <div>
          {/* Summary strip */}
          <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-4 bg-emerald-50/30">
            <div className="text-xs">
              <span className="text-slate-500">Total assigned:</span>{" "}
              <span className="font-mono font-semibold text-slate-900">{data.summary.total_assigned}</span>
            </div>
            <div className="text-xs">
              <span className="text-emerald-700 font-semibold">Visible in API:</span>{" "}
              <span className="font-mono font-semibold text-emerald-900">{data.summary.visible}</span>
            </div>
            <div className="text-xs">
              <span className="text-rose-700 font-semibold">Filtered out:</span>{" "}
              <span className="font-mono font-semibold text-rose-900">{data.summary.filtered}</span>
            </div>
            {data.summary.filtered > 0 && (
              <label className="text-xs ml-auto flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showOnlyFiltered}
                  onChange={(e) => setShowOnlyFiltered(e.target.checked)}
                  data-testid="api-preview-show-only-filtered"
                />
                Show only filtered ({data.summary.filtered})
              </label>
            )}
          </div>

          {/* Table */}
          <div className="max-h-[420px] overflow-auto" data-testid="api-preview-rows">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold w-8"></th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">Network</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">Mode</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">Health</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-400">
                      {showOnlyFiltered ? "No brands were filtered — everything's visible." : "No brand assignments yet."}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => {
                    // Freshness chip: green if <8h, amber 8-24h, red >24h, slate if never.
                    let chip = { txt: "never checked", cls: "bg-slate-100 text-slate-500" };
                    if (r.last_health_check_at) {
                      const ageMin = (Date.now() - new Date(r.last_health_check_at).getTime()) / 60000;
                      if (r.last_health_status === "disabled") {
                        chip = { txt: "disabled", cls: "bg-rose-100 text-rose-700" };
                      } else if (r.last_health_status === "error") {
                        chip = { txt: "error", cls: "bg-amber-100 text-amber-700" };
                      } else if (ageMin < 60) {
                        chip = { txt: `${Math.round(ageMin)}m ago`, cls: "bg-emerald-100 text-emerald-700" };
                      } else if (ageMin < 480) {
                        chip = { txt: `${Math.round(ageMin / 60)}h ago`, cls: "bg-emerald-100 text-emerald-700" };
                      } else if (ageMin < 1440) {
                        chip = { txt: `${Math.round(ageMin / 60)}h ago`, cls: "bg-amber-100 text-amber-700" };
                      } else {
                        chip = { txt: `${Math.round(ageMin / 1440)}d ago`, cls: "bg-rose-100 text-rose-700" };
                      }
                    }
                    const checking = recheckingIds.has(r.brand_id);
                    return (
                      <tr key={r.brand_id} className="border-b border-slate-100" data-testid={`api-preview-row-${r.brand_id}`}>
                        <td className="px-3 py-2">
                          {r.visible
                            ? <CheckCircle size={16} className="text-emerald-600" weight="fill" />
                            : <WarningCircle size={16} className="text-rose-600" weight="fill" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{r.brand_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{r.brand_id}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${NETWORK_BADGE[r.network] || NETWORK_BADGE[""]}`}>
                            {r.network || "(unknown)"}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                          {r.payout_mode}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${chip.cls}`} title={r.last_health_check_at || ""}>
                              {chip.txt}
                            </span>
                            {r.network === "mrge" && (
                              <button
                                type="button"
                                onClick={() => recheckOne(r.brand_id)}
                                disabled={checking}
                                className="p-1 text-slate-400 hover:text-[#002FA7] disabled:opacity-40"
                                title="Re-check upstream now"
                                data-testid={`api-preview-recheck-${r.brand_id}`}
                              >
                                <ArrowsClockwise size={12} className={checking ? "animate-spin" : ""} />
                              </button>
                            )}
                          </div>
                          {r.upstream_rate != null && (
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5">upstream: {r.upstream_rate}%</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-600 max-w-md">
                          {r.visible
                            ? <span className="text-emerald-700">ok</span>
                            : <span className="text-rose-700">{r.reason}</span>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
